import { supabase } from '../config/supabase';
import {
  SupportTicket,
  SupportTicketWithDetails,
  TicketComment,
  TicketPriority,
  TicketStatus,
  TicketFilters
} from '../types';

// Helper to convert DB row to SupportTicket type
const toSupportTicket = (row: Record<string, unknown>): SupportTicket => ({
  id: row.id as string,
  organizationId: row.organization_id as string | undefined,
  subject: row.subject as string,
  description: row.description as string | undefined,
  priority: row.priority as TicketPriority,
  status: row.status as TicketStatus,
  createdBy: row.created_by as string | undefined,
  assignedTo: row.assigned_to as string | undefined,
  resolvedAt: row.resolved_at ? new Date(row.resolved_at as string) : undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string)
});

const toTicketComment = (row: Record<string, unknown>): TicketComment => ({
  id: row.id as string,
  ticketId: row.ticket_id as string,
  authorId: row.author_id as string | undefined,
  content: row.content as string,
  isInternal: row.is_internal as boolean,
  createdAt: new Date(row.created_at as string)
});

export const supportTicketsService = {
  // Get all tickets (super_admin) or organization's tickets (client)
  async getAll(filters?: TicketFilters): Promise<SupportTicket[]> {
    let query = supabase
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.organizationId) {
      query = query.eq('organization_id', filters.organizationId);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.priority) {
      query = query.eq('priority', filters.priority);
    }

    if (filters?.search) {
      query = query.or(`subject.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching tickets:', error);
      throw new Error('Impossible de charger les tickets');
    }

    return (data || []).map(toSupportTicket);
  },

  // Get ticket by ID with details
  async getById(id: string): Promise<SupportTicketWithDetails | null> {
    const { data: ticketRow, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching ticket:', error);
      throw new Error('Impossible de charger le ticket');
    }

    if (!ticketRow) return null;

    const ticket = toSupportTicket(ticketRow);

    // Get organization if exists
    let organization;
    if (ticket.organizationId) {
      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', ticket.organizationId)
        .single();

      if (orgData) {
        organization = {
          id: orgData.id,
          name: orgData.name,
          slug: orgData.slug,
          contactName: orgData.contact_name,
          contactEmail: orgData.contact_email,
          contactPhone: orgData.contact_phone,
          logoUrl: orgData.logo_url,
          address: orgData.address,
          notes: orgData.notes,
          status: orgData.status,
          contractStart: orgData.contract_start ? new Date(orgData.contract_start) : undefined,
          contractEnd: orgData.contract_end ? new Date(orgData.contract_end) : undefined,
          createdAt: new Date(orgData.created_at),
          updatedAt: new Date(orgData.updated_at)
        };
      }
    }

    // Get comments
    const { data: commentsData } = await supabase
      .from('ticket_comments')
      .select('*')
      .eq('ticket_id', id)
      .order('created_at', { ascending: true });

    // Get creator and assignee info
    let creator, assignee;
    if (ticket.createdBy) {
      const { data: creatorData } = await supabase
        .from('users')
        .select('id, email, display_name, role')
        .eq('id', ticket.createdBy)
        .single();

      if (creatorData) {
        creator = {
          id: creatorData.id,
          email: creatorData.email,
          role: creatorData.role,
          organizationId: null,
          displayName: creatorData.display_name,
          createdAt: new Date()
        };
      }
    }

    if (ticket.assignedTo) {
      const { data: assigneeData } = await supabase
        .from('users')
        .select('id, email, display_name, role')
        .eq('id', ticket.assignedTo)
        .single();

      if (assigneeData) {
        assignee = {
          id: assigneeData.id,
          email: assigneeData.email,
          role: assigneeData.role,
          organizationId: null,
          displayName: assigneeData.display_name,
          createdAt: new Date()
        };
      }
    }

    return {
      ...ticket,
      organization,
      comments: (commentsData || []).map(toTicketComment),
      creator,
      assignee
    };
  },

  // Create ticket
  async create(ticket: Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt'>): Promise<SupportTicket> {
    const { data, error } = await supabase
      .from('support_tickets')
      .insert({
        organization_id: ticket.organizationId,
        subject: ticket.subject,
        description: ticket.description,
        priority: ticket.priority || 'normal',
        status: ticket.status || 'open',
        created_by: ticket.createdBy,
        assigned_to: ticket.assignedTo
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating ticket:', error);
      throw new Error('Impossible de créer le ticket');
    }

    return toSupportTicket(data);
  },

  // Update ticket
  async update(id: string, updates: Partial<Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt'>>): Promise<SupportTicket> {
    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    };

    if (updates.organizationId !== undefined) updateData.organization_id = updates.organizationId;
    if (updates.subject !== undefined) updateData.subject = updates.subject;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.status !== undefined) {
      updateData.status = updates.status;
      if (updates.status === 'resolved' || updates.status === 'closed') {
        updateData.resolved_at = new Date().toISOString();
      }
    }
    if (updates.assignedTo !== undefined) updateData.assigned_to = updates.assignedTo;

    const { data, error } = await supabase
      .from('support_tickets')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating ticket:', error);
      throw new Error('Impossible de mettre à jour le ticket');
    }

    return toSupportTicket(data);
  },

  // Update status only
  async updateStatus(id: string, status: TicketStatus): Promise<void> {
    const updateData: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'resolved' || status === 'closed') {
      updateData.resolved_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('support_tickets')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('Error updating ticket status:', error);
      throw new Error('Impossible de mettre à jour le statut');
    }
  },

  // Delete ticket
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting ticket:', error);
      throw new Error('Impossible de supprimer le ticket');
    }
  },

  // Add comment to ticket
  async addComment(ticketId: string, authorId: string, content: string, isInternal: boolean = false): Promise<TicketComment> {
    const { data, error } = await supabase
      .from('ticket_comments')
      .insert({
        ticket_id: ticketId,
        author_id: authorId,
        content,
        is_internal: isInternal
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding comment:', error);
      throw new Error('Impossible d\'ajouter le commentaire');
    }

    // Update ticket updated_at
    await supabase
      .from('support_tickets')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    return toTicketComment(data);
  },

  // Get comments for a ticket
  async getComments(ticketId: string): Promise<TicketComment[]> {
    const { data, error } = await supabase
      .from('ticket_comments')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching comments:', error);
      throw new Error('Impossible de charger les commentaires');
    }

    return (data || []).map(toTicketComment);
  },

  // Get tickets by organization
  async getByOrganization(organizationId: string): Promise<SupportTicket[]> {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tickets:', error);
      throw new Error('Impossible de charger les tickets');
    }

    return (data || []).map(toSupportTicket);
  },

  // Get ticket stats
  async getStats(organizationId?: string): Promise<{
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
  }> {
    let query = supabase.from('support_tickets').select('status');

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching ticket stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const tickets = data || [];
    return {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      inProgress: tickets.filter(t => t.status === 'in_progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      closed: tickets.filter(t => t.status === 'closed').length
    };
  }
};
